// import { useBottomNav } from "@/contexts/BottomNavContext";
import { XIcon, ArrowLeftIcon } from 'lucide-react'
import React, { ReactNode, useState, useEffect, useRef } from 'react'
import { Drawer as VaulDrawer } from 'vaul'
import { lockScroll, unlockScroll, forceUnlockScroll } from '@/utils/scrollLock'

export interface DrawerProps {
    /** The content to be rendered inside the drawer */
    children: ReactNode

    /** The trigger button content */
    triggerContent: ReactNode

    /** Custom classes for the trigger button */
    triggerClassName?: string

    /** Classes to be applied to the drawer content */
    contentClassName?: string

    /** Classes to be applied to the overlay/backdrop element */
    overlayClassName?: string

    /** Control if drawer is open or closed externally */
    open?: boolean

    /** Handler for open state changes */
    onOpenChange?: (open: boolean) => void

    /** Whether the drawer should close when pressing the escape key */
    shouldCloseOnEscapePress?: boolean

    /** Whether the drawer should close when pressing the backdrop */
    shouldCloseOnOutsideClick?: boolean

    /** Position of the drawer - 'bottom' for bottom sheet, 'center' for modal */
    position?: 'bottom' | 'center'

    /** Drawer header configuration */
    drawerHeader?: DrawerHeaderProps

    /** Height mode - 'auto' fits content, 'fixed' uses fixed height */
    heightMode?: 'auto' | 'fixed'

    /** Fixed height value when heightMode is 'fixed' (e.g., '85dvh', '95dvh') */
    fixedHeight?: string
}

interface DrawerHeaderProps {
    title?: string
    showArrow?: boolean
    showClose?: boolean
    onClose?: () => void
    onClick?: () => void
}

export const Drawer: React.FC<DrawerProps> = ({
    children,
    triggerContent,
    triggerClassName = '',
    contentClassName = '',
    overlayClassName = '',
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    position = 'bottom',
    drawerHeader,
    heightMode = 'auto',
    fixedHeight = '85dvh',
}) => {
    // const { isBottomNavVisible } = useBottomNav();
    const [internalOpen, setInternalOpen] = useState(false)
    const drawerContentRef = useRef<HTMLDivElement>(null)

    const isControlled = controlledOpen !== undefined
    const open = isControlled ? controlledOpen : internalOpen

    const onOpenChange = (newOpen: boolean) => {
        if (!isControlled) {
            setInternalOpen(newOpen)
        }

        controlledOnOpenChange?.(newOpen)
    }

    // Handle scroll locking when drawer opens/closes
    useEffect(() => {
        if (open) {
            lockScroll()
        } else {
            unlockScroll()
        }

        // Cleanup function to ensure scroll is unlocked on unmount
        return () => {
            if (open) {
                unlockScroll()
            }
        }
    }, [open])

    // Handle viewport changes (keyboard appearance/disappearance)
    useEffect(() => {
        if (!open) return

        // Capture current ref value for cleanup
        const drawerElement = drawerContentRef.current

        const handleInputFocus = (event: FocusEvent) => {
            const target = event.target as
                | HTMLInputElement
                | HTMLTextAreaElement
            if (!target || !['INPUT', 'TEXTAREA'].includes(target.tagName))
                return

            // Prevent iOS zoom on input focus
            target.style.fontSize = '16px'
        }

        // Event listeners
        const addEventListeners = () => {
            if (drawerElement) {
                drawerElement.addEventListener('focusin', handleInputFocus, {
                    passive: true,
                })
            }
        }

        addEventListeners()

        return () => {
            if (drawerElement) {
                drawerElement.removeEventListener('focusin', handleInputFocus)
            }
        }
    }, [open, position])

    // Force unlock scroll on component unmount
    useEffect(() => {
        return () => {
            forceUnlockScroll()
        }
    }, [])

    return (
        <VaulDrawer.Root
            open={open}
            onOpenChange={onOpenChange}
            shouldScaleBackground={false}
            preventScrollRestoration={true}
            repositionInputs={false}
        >
            <VaulDrawer.Trigger className={triggerClassName}>
                {triggerContent}
            </VaulDrawer.Trigger>
            <VaulDrawer.Portal>
                <VaulDrawer.Overlay
                    className={`fixed inset-0 z-30 bg-black/30 backdrop-blur-[2px] ${overlayClassName}`}
                />
                <VaulDrawer.Content
                    ref={drawerContentRef}
                    className={`text-16 fixed z-30 touch-pan-y overflow-x-hidden scroll-smooth bg-white outline-none select-text md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl md:shadow-2xl ${heightMode === 'fixed'
                            ? `flex flex-col overflow-hidden`
                            : 'overflow-y-auto'
                        } ${heightMode === 'fixed'
                            ? `h-[${fixedHeight}] md:h-[${fixedHeight}]`
                            : 'h-auto max-h-[85dvh] min-h-[200px] md:h-fit md:max-h-[85dvh]'
                        } ${position === 'center'
                            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl shadow-2xl'
                            : 'right-0 bottom-0 left-0 rounded-t-2xl'
                        } ${contentClassName} `}
                    style={{
                        WebkitOverflowScrolling: 'touch',
                        WebkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',
                        contain: 'layout style paint',
                        WebkitUserSelect: 'text',
                        ...(heightMode === 'fixed' && { height: fixedHeight }),
                    }}
                    aria-describedby="drawer"
                >
                    {drawerHeader && <DrawerHeader {...drawerHeader} />}
                    <div
                        className={`px-2 py-2 md:px-4 md:py-4 ${heightMode === 'fixed'
                                ? 'flex-1 flex flex-col min-h-0'
                                : ''
                            }`}
                    >
                        {children}
                    </div>
                </VaulDrawer.Content>
            </VaulDrawer.Portal>
        </VaulDrawer.Root>
    )
}

const DrawerHeader = ({
    onClick,
    title = 'Add to collection',
    subtitle,
    showArrow = true,
    showClose = false,
    onClose,
}: {
    showArrow?: boolean
    title?: string
    subtitle?: string
    showClose?: boolean
    onClose?: () => void
    onClick?: () => void
}) => (
    <div
        className={`sticky top-0 z-30 bg-white py-4 ${showClose ? 'border-b border-[#f0f0f0]' : 'px-2 md:px-4'}`}
    >
        {showArrow && (
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onClick?.()
                }}
                className="absolute top-4 left-4 cursor-pointer rounded-full transition-colors hover:bg-gray-100"
            >
                <ArrowLeftIcon width={24} height={24} stroke="black" />
            </button>
        )}
        <VaulDrawer.Title>
            <div className="flex flex-col items-center gap-2 px-2 text-center md:px-16 md:text-left">
                <h4 className="flex-1 text-center text-lg font-semibold">
                    {title}
                </h4>
                {subtitle && (
                    <p className="text-sm text-gray-500">{subtitle}</p>
                )}
            </div>
        </VaulDrawer.Title>
        {showClose && (
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onClose?.()
                }}
                className="absolute top-4 right-4 cursor-pointer"
            >
                <XIcon width={24} height={24} stroke="black" />
            </button>
        )}
    </div>
)
